import { Stack, Text } from "@mantine/core";
import {
  IconAdjustments,
  IconBuildingStore,
  IconBox,
  IconHierarchy,
  IconSettingsCog,
  IconVersions,
} from "@tabler/icons-react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  filterTutorialSections,
  TutorialPageShell,
  TutorialSection,
  TutorialSectionCard,
} from "@components/tutorial/TutorialShared";

export default function AdminTutorialPage() {
  const { t } = useTranslation("tutorial");
  const [query, setQuery] = useState("");
  const sections: TutorialSection[] = [
    {
      title: t("adminTutorialPage.sections.adminDashboard.title"),
      description: t("adminTutorialPage.sections.adminDashboard.description"),
      icon: <IconHierarchy size={18} />,
      actions: [
        {
          title: t("adminTutorialPage.sections.adminDashboard.actions.openAdminWorkspace.title"),
          location: t(
            "adminTutorialPage.sections.adminDashboard.actions.openAdminWorkspace.location",
          ),
          steps: t("adminTutorialPage.sections.adminDashboard.actions.openAdminWorkspace.steps", {
            returnObjects: true,
          }) as string[],
        },
        {
          title: t("adminTutorialPage.sections.adminDashboard.actions.openDistributorView.title"),
          location: t(
            "adminTutorialPage.sections.adminDashboard.actions.openDistributorView.location",
          ),
          steps: t("adminTutorialPage.sections.adminDashboard.actions.openDistributorView.steps", {
            returnObjects: true,
          }) as string[],
        },
      ],
    },
    {
      title: t("adminTutorialPage.sections.scopeVersionControls.title"),
      description: t("adminTutorialPage.sections.scopeVersionControls.description"),
      icon: <IconVersions size={18} />,
      actions: [
        {
          title: t("adminTutorialPage.sections.scopeVersionControls.actions.manageScopes.title"),
          location: t(
            "adminTutorialPage.sections.scopeVersionControls.actions.manageScopes.location",
          ),
          steps: t("adminTutorialPage.sections.scopeVersionControls.actions.manageScopes.steps", {
            returnObjects: true,
          }) as string[],
        },
        {
          title: t("adminTutorialPage.sections.scopeVersionControls.actions.manageVersions.title"),
          location: t(
            "adminTutorialPage.sections.scopeVersionControls.actions.manageVersions.location",
          ),
          steps: t("adminTutorialPage.sections.scopeVersionControls.actions.manageVersions.steps", {
            returnObjects: true,
          }) as string[],
        },
      ],
    },
    {
      title: t("adminTutorialPage.sections.distributorManagement.title"),
      description: t("adminTutorialPage.sections.distributorManagement.description"),
      icon: <IconBuildingStore size={18} />,
      actions: [
        {
          title: t("adminTutorialPage.sections.distributorManagement.actions.addDistributor.title"),
          location: t(
            "adminTutorialPage.sections.distributorManagement.actions.addDistributor.location",
          ),
          steps: t(
            "adminTutorialPage.sections.distributorManagement.actions.addDistributor.steps",
            { returnObjects: true },
          ) as string[],
        },
        {
          title: t(
            "adminTutorialPage.sections.distributorManagement.actions.editDistributor.title",
          ),
          location: t(
            "adminTutorialPage.sections.distributorManagement.actions.editDistributor.location",
          ),
          steps: t(
            "adminTutorialPage.sections.distributorManagement.actions.editDistributor.steps",
            { returnObjects: true },
          ) as string[],
        },
        {
          title: t("adminTutorialPage.sections.distributorManagement.actions.rowActions.title"),
          location: t(
            "adminTutorialPage.sections.distributorManagement.actions.rowActions.location",
          ),
          steps: t("adminTutorialPage.sections.distributorManagement.actions.rowActions.steps", {
            returnObjects: true,
          }) as string[],
        },
      ],
    },
    {
      title: t("adminTutorialPage.sections.productConfiguration.title"),
      description: t("adminTutorialPage.sections.productConfiguration.description"),
      icon: <IconBox size={18} />,
      actions: [
        {
          title: t(
            "adminTutorialPage.sections.productConfiguration.actions.openProductPages.title",
          ),
          location: t(
            "adminTutorialPage.sections.productConfiguration.actions.openProductPages.location",
          ),
          steps: t(
            "adminTutorialPage.sections.productConfiguration.actions.openProductPages.steps",
            { returnObjects: true },
          ) as string[],
          note: t("adminTutorialPage.sections.productConfiguration.actions.openProductPages.note"),
        },
      ],
    },
    {
      title: t("adminTutorialPage.sections.adminRequestHandling.title"),
      description: t("adminTutorialPage.sections.adminRequestHandling.description"),
      icon: <IconAdjustments size={18} />,
      actions: [
        {
          title: t("adminTutorialPage.sections.adminRequestHandling.actions.reviewRequests.title"),
          location: t(
            "adminTutorialPage.sections.adminRequestHandling.actions.reviewRequests.location",
          ),
          steps: t("adminTutorialPage.sections.adminRequestHandling.actions.reviewRequests.steps", {
            returnObjects: true,
          }) as string[],
        },
      ],
    },
    {
      title: t("adminTutorialPage.sections.sharedUserWorkflows.title"),
      description: t("adminTutorialPage.sections.sharedUserWorkflows.description"),
      icon: <IconSettingsCog size={18} />,
      actions: [
        {
          title: t(
            "adminTutorialPage.sections.sharedUserWorkflows.actions.useSharedTutorial.title",
          ),
          location: t(
            "adminTutorialPage.sections.sharedUserWorkflows.actions.useSharedTutorial.location",
          ),
          steps: t(
            "adminTutorialPage.sections.sharedUserWorkflows.actions.useSharedTutorial.steps",
            { returnObjects: true },
          ) as string[],
        },
      ],
    },
  ];

  const filteredSections = useMemo(
    () => filterTutorialSections(sections, query),
    [sections, query],
  );
  const actionCount = filteredSections.reduce(
    (count, section) => count + section.actions.length,
    0,
  );

  return (
    <TutorialPageShell
      badge={t("adminTutorialPage.badge")}
      title={t("adminTutorialPage.title")}
      description={t("adminTutorialPage.description")}
      searchValue={query}
      onSearchChange={setQuery}
      resultCount={actionCount}
      relatedLinks={[
        {
          label: t("adminTutorialPage.relatedLinks.backToShared.label"),
          to: "/tutorial",
          description: t("adminTutorialPage.relatedLinks.backToShared.description"),
        },
        {
          label: t("adminTutorialPage.relatedLinks.distributorTutorial.label"),
          to: "/tutorial/distributor",
          description: t("adminTutorialPage.relatedLinks.distributorTutorial.description"),
        },
      ]}
    >
      <Stack gap="xl">
        {filteredSections.length === 0 ? (
          <Text c="dimmed">{t("adminTutorialPage.noResults")}</Text>
        ) : null}
        {filteredSections.map((section) => (
          <TutorialSectionCard key={section.title} section={section} />
        ))}
      </Stack>
    </TutorialPageShell>
  );
}
